import { Box, Typography, Button, Container, Paper, CircularProgress, Snackbar, Alert, useMediaQuery, useTheme, Grid, Link, TextField, Collapse } from "@mui/material";
import { useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthService } from '../services/authService';
import { createSubscription, createCustomer, updateUserPlanType } from '../services/stripeService';
// tracking removed
import { PRICE_IDS, PLANS } from '../services/PriceIdService';
import { landingSettings, isPromoEnabled, getPromoCode, getPromoTrialDays, isValidPromo } from '../services/landing-settings';
import Header from "../components/Header";
import Footer from "../components/Footer";
import backdrop from "../img/backdrop.png";

// REMOVE THIS LINE WHEN LAUNCHING: Set to false when subscription is live
const SUBSCRIPTION_COMING_SOON = false;

function Subscribe() {
  const [error, setError] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [showPromoCode, setShowPromoCode] = useState(false);
  const { user, isAuthenticated, checkAuthState } = useAuthService();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Scroll to top on mobile when component mounts
  useEffect(() => {
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, [isMobile]);

  // Re-check auth state on mount if needed
  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  // Prefill promo code from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const promo = params.get('promo');
    if (promo && isPromoEnabled()) {
      setPromoCode(promo.toUpperCase());
      setShowPromoCode(true);
    }
  }, []);

  const handleSubscribe = async (priceId, planName) => {
    if (!isAuthenticated || !user) {
        setError("Please log in or sign up to subscribe.");
        navigate('/login'); // Redirect to login if not authenticated
        return;
    }
    
    // DEBUG: Log all price IDs
    console.log('PRICE_IDS debug:', {
      member: PRICE_IDS.member,
      currentPriceId: priceId
    });
    
    setLoadingPlan(planName);
    setError(null);

    try {
      const userEmail = user.attributes?.email;
      if (!userEmail) {
        throw new Error('User email not found');
      }

      // Create or get customer
      const customer = await createCustomer(userEmail, user.userId);
      if (!customer || !customer.id) {
         throw new Error('Could not create or retrieve customer information.');
      }
      // tracking removed
      
      // Map the planName to the correct planType for DynamoDB
      let planType = 'member'; // Always use member plan type
      
      console.log(`Selected plan: ${planName}, mapped to planType: ${planType}, priceId: ${priceId}`);
      
      // Update the user's planType in DynamoDB using the direct POST method
      try {
        console.log(`Calling updateUserPlanType with userId=${user.userId}, planType=${planType}`);
        const result = await updateUserPlanType(user.userId, planType);
        console.log('Plan type update result:', result);
      } catch (dbError) {
        console.error('Error updating plan type in DynamoDB:', dbError);
        // Continue with subscription creation even if this fails
      }
      
      // Determine promo and trial
      const normalizedPromo = (promoCode || "").toUpperCase();
      const promoValid = isPromoEnabled() && isValidPromo(normalizedPromo);
      const trialDays = promoValid ? getPromoTrialDays() : 0;
      const metadata = {
        planType,
        ...(promoValid ? { promoCode: normalizedPromo } : {})
      };

      // Create subscription using the selected priceId, passing metadata and trial days
      await createSubscription(userEmail, user.userId, priceId, metadata, trialDays);
      
      // Note: The user will be redirected to Stripe Checkout
      
    } catch (error) {
      console.error('Subscription error:', error);
      setError(error.message || 'Failed to process subscription. Please try again.');
    } finally {
      setLoadingPlan(null); // Reset loading state regardless of outcome
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <HelmetProvider>
      <>
        <Helmet>
          <title>Subscribe to Screamify™ | Support Independent Horror</title>
          <meta name="description" content="Choose your Screamify plan and support independent horror filmmakers. Join our community and access exclusive horror content." />
          <meta name="keywords" content="horror streaming subscription, horror platform pricing, indie horror support, horror community" />
          <meta name="robots" content="index, follow" />
        </Helmet>
        <Box 
          component="main"
          role="main"
          sx={{ 
            width: '100%', 
            minHeight: '100vh',
            backgroundImage: `url(${backdrop})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 0
            }
          }}
        >
          <Header />
          <Container 
            maxWidth="lg" 
            sx={{ 
              flex: 1, 
              py: { xs: 4, md: 8 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center', 
              mt: { xs: 8, md: 4 },
              position: 'relative',
              zIndex: 1
            }}
          >
             <Typography 
                variant={isMobile ? "h4" : "h3"} 
                component="h1" 
                align="center" 
                sx={{ 
                    color: '#ff0000', 
                    fontWeight: 'bold', 
                    textShadow: '0 0 14px rgba(255,0,0,0.45)', 
                    letterSpacing: '0.5px',
                    mb: 6
                }}
             >
               Support Independent Horror
            </Typography>

            <Grid container spacing={isMobile ? 3 : 4} justifyContent="center" alignItems="stretch">
              {PLANS.map((plan) => (
                <Grid item key={plan.id} xs={12} sm={8} md={5} lg={4} sx={{ display: 'flex' }}>
                  <Paper
                    elevation={4}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(12px)',
                      p: { xs: 2.5, sm: 4 },
                      border: '1px solid rgba(255, 0, 0, 0.25)',
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      width: '100%',
                      maxWidth: { xs: 420, sm: 480 },
                      mx: 'auto',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: '0 6px 28px rgba(0, 0, 0, 0.35)',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 8px 40px rgba(255, 0, 0, 0.2)'
                      },
                      color: 'white'
                    }}
                  >
                    <Box>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '1.3rem',
                            mb: 2,
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            textAlign: 'center'
                            }}
                        >
                            {plan.name}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 1, mb: 3 }}>
                          <Typography
                              variant="h4"
                              sx={{
                              color: 'rgba(255, 255, 255, 0.95)',
                              fontSize: { xs: '2rem', sm: '2.2rem' },
                              fontWeight: 700,
                              letterSpacing: '0.5px',
                              textAlign: 'center'
                              }}
                          >
                              {plan.price}
                          </Typography>
                          <Typography
                              sx={{
                              color: 'rgba(255, 255, 255, 0.8)',
                              fontSize: { xs: '1rem', sm: '1.1rem' },
                              letterSpacing: '0.5px',
                              textAlign: 'center'
                              }}
                          >
                              {plan.period}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 3 }}>
                            {plan.features.map((feature) => (
                            <Box
                                key={feature}
                                sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                mb: 1.5,
                                color: 'rgba(255, 255, 255, 0.9)',
                                letterSpacing: '0.3px',
                                fontSize: { xs: '0.95rem', sm: '0.9rem' }
                                }}
                            >
                                <Box
                                sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    bgcolor: '#ff0000',
                                    mr: 1,
                                    flexShrink: 0,
                                    boxShadow: '0 0 8px rgba(255, 0, 0, 0.3)'
                                }}
                                />
                                <Box component="span" sx={{ textAlign: 'left' }}>
                                  {feature}
                                </Box>
                            </Box>
                            ))}
                        </Box>
                        {/* Promo code block moved below CTA button for centered emphasis */}
                    </Box>

                    <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => handleSubscribe(plan.priceId, plan.name)}
                            disabled={!isAuthenticated || loadingPlan === plan.name}
                            sx={{
                                bgcolor: '#ff0000',
                                backgroundImage: 'linear-gradient(45deg, #ff0000, #cc0000)',
                                color: '#ffffff',
                                py: { xs: 1.6, sm: 1.8 },
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                textTransform: 'none',
                                borderRadius: '6px',
                                boxShadow: '0 3px 10px rgba(255, 0, 0, 0.25)',
                                letterSpacing: '0.5px',
                                border: '1px solid rgba(255, 0, 0, 0.3)',
                                '&:hover': {
                                bgcolor: '#cc0000',
                                backgroundImage: 'linear-gradient(45deg, #cc0000, #990000)',
                                boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)'
                                },
                                '&:active': {
                                bgcolor: '#990000',
                                transform: 'scale(0.98)'
                                },
                                '&.Mui-disabled': {
                                bgcolor: 'rgba(255, 0, 0, 0.5)',
                                color: 'rgba(255, 255, 255, 0.7)'
                                }
                            }}
                        >
                            {loadingPlan === plan.name ? (
                                <CircularProgress size={24} color="inherit" /> 
                            ) : !isAuthenticated ? (
                                 'Login to Subscribe'
                            ) : (
                                'Join Now'
                            )}
                        </Button>

                        {isPromoEnabled() && (
                          <Box sx={{ mt: 2 }}>
                            <Button 
                              variant="text" 
                              onClick={() => setShowPromoCode(!showPromoCode)}
                              sx={{ color: '#ff0000', textTransform: 'none', fontWeight: 'bold' }}
                            >
                              Have a promo code?
                            </Button>
                            <Collapse in={showPromoCode}>
                              <Box sx={{ maxWidth: 300, mx: 'auto' }}>
                                <TextField
                                  placeholder="Enter promo code"
                                  fullWidth
                                  size="small"
                                  value={promoCode}
                                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                  InputProps={{ sx: { backgroundColor: 'rgba(255,255,255,0.92)' } }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': { borderColor: 'rgba(255, 0, 0, 0.4)' },
                                      '&:hover fieldset': { borderColor: '#ff0000' },
                                      '&.Mui-focused fieldset': { borderColor: '#ff0000' }
                                    }
                                  }}
                                  helperText={promoCode && isValidPromo(promoCode) ? `${getPromoTrialDays()} day free trial applied` : (promoCode ? 'Invalid code' : ' ')}
                                  FormHelperTextProps={{ sx: { textAlign: 'center', color: promoCode && promoCode.toUpperCase() === getPromoCode() ? '#ff0000' : '#b0b0b0' } }}
                                />
                              </Box>
                            </Collapse>
                          </Box>
                        )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
          <Footer />
          
          <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
            <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
        </Box>
      </>
    </HelmetProvider>
  );
}

export default Subscribe; 